import React, { useState } from 'react';
import { Edit } from '@mui/icons-material';
import { Button, CircularProgress } from '@mui/material';
import { usePermissionCheck } from '../../../../../core/hooks/usePermissionCheck';
import { useHierarchy, HierarchyTreeNode } from 'core/hooks/useHierarchy';
import styles from '../GeneralSettings.module.scss';

interface HierarchyListProps {
	onEditClick: (nodeId: string) => void;
	inFocus:
		| 'organization'
		| 'workplace'
		| 'user'
		| 'organizational_hierarchy'
		| 'security_role'
		| 'partners'
		| null;
}

const HierarchyList: React.FC<HierarchyListProps> = ({ onEditClick, inFocus }) => {
	const { hasEditPermission } = usePermissionCheck();
	const canEditHierarchy = hasEditPermission('Hierarchy');

	// Use static params to prevent infinite loops
	const { hierarchyData, loading, error, buildHierarchyTree, refetch } = useHierarchy({
		page: 1,
		itemsPerPage: 50,
		inFocus,
	});
	// console.log("hierarchy data", hierarchyData);

	// Helper functions for display and rendering a flat list (organization + workplaces)
	const getParentName = (node: HierarchyTreeNode): string => {
		if (!node.parent) return node.type === 'organization' ? '-' : 'Organization';
		const pname = node.parent.name || '';
		// Remove any parenthetical content and standalone 'node' from parent name
		let cleaned = pname.replace(/\s*\([^)]*\)\s*/g, ' ');
		cleaned = cleaned.replace(/\bnode\b/gi, ' ');
		return cleaned.replace(/\s+/g, ' ').trim() || (node.type === 'organization' ? '-' : 'Organization');
	};

	// Helper functions
	const getDisplayId = (node: HierarchyTreeNode): string => {
		if (node.type === 'workplace') {
			const name = node.name || '';
			const match = name.match(/\(([^)]+)\)/);
			if (match && match[1]) {
				const extracted = match[1].trim();
				if (/^\d+$/.test(extracted)) return extracted.padStart(3, '0');
				return extracted;
			}
		}
		return '';
	};

	const getDisplayName = (node: HierarchyTreeNode): string => {
		const name = node.name || '';
		let cleaned = name.replace(/\s*\([^)]*\)\s*/g, ' ');
		cleaned = cleaned.replace(/\bnode\b/gi, ' ');
		return cleaned.replace(/\s+/g, ' ').trim();
	};

	const renderTableRow = (node: HierarchyTreeNode, isTop = false): JSX.Element => (
		<tr key={node.id}>
			<td style={{ whiteSpace: 'nowrap' }}>{isTop ? '' : getDisplayId(node)}</td>
			<td style={{ width: '30%', minWidth: '100px' }}>{getDisplayName(node)}</td>
			<td style={{ width: '25%', minWidth: '150px', whiteSpace: 'nowrap' }}>
				{getParentName(node)}
			</td>
			<td style={{ padding: '8px', width: '48px' }}>
				<div style={{ display: 'flex', justifyContent: 'center' }}>
					{isTop ? (
						<button
							className={styles.iconButton}
							onClick={() => onEditClick(`${node.type}-${node.id}`)}
							title={canEditHierarchy ? 'Edit hierarchy' : 'You do not have permission to edit'}
							disabled={!canEditHierarchy}
							style={{
								opacity: canEditHierarchy ? 1 : 0.5,
								cursor: canEditHierarchy ? 'pointer' : 'not-allowed',
							}}
						>
							<Edit className={styles.editIcon} />
						</button>
					) : null}
				</div>
			</td>
		</tr>
	);

	const collectWorkplaces = (nodes: HierarchyTreeNode[] = []): HierarchyTreeNode[] => {
		const result: HierarchyTreeNode[] = [];
		const walk = (nList: HierarchyTreeNode[]) => {
			for (const n of nList) {
				if (n.type === 'workplace') result.push(n);
				if (n.children && n.children.length > 0) walk(n.children);
			}
		};
		walk(nodes);
		return result;
	};

	const hierarchyTree = buildHierarchyTree();

	// Debug information (can be removed in production)
	const debugInfo = hierarchyData
		? {
				hasOrganization: !!hierarchyData.organization,
				hierarchyNodesCount: hierarchyData.hierarchyNodes.length,
				workplacesCount: hierarchyData.workplaces.length,
				totalNodes: hierarchyData.combinedHierarchy.length,
			}
		: null;

	if (loading) {
		return (
			<div
				className={styles.tableContainer}
				style={{
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					minHeight: '200px',
				}}
			>
				<CircularProgress size={20} color="inherit" />
				</div>
		);
	}

	if (error) {
		return (
			<div
				className={styles.tableContainer}
				style={{
					padding: '20px',
					textAlign: 'center',
					color: 'red',
				}}
			>
				<p>Error loading hierarchy: {error}</p>
				<Button onClick={refetch}>Retry</Button>
			</div>
		);
	}

	if (hierarchyTree.length === 0) {
		return (
			<div
				className={styles.tableContainer}
				style={{
					padding: '20px',
					textAlign: 'center',
					color: '#666',
				}}
			>
				<p>No organization or workplace data found.</p>
				<p>Make sure your organization and workplaces are properly configured.</p>
				{debugInfo && (
					<details style={{ marginTop: '10px', fontSize: '12px', color: '#999' }}>
						<summary>Debug Info</summary>
						<pre>{JSON.stringify(debugInfo, null, 2)}</pre>
					</details>
				)}
			</div>
		);
	}

	// Build flat list: first organization node (if any), then all workplaces sorted alphabetically by unit number
	const organizationNode = hierarchyTree.find((n) => n.type === 'organization');
	const workplaces = collectWorkplaces(hierarchyTree).sort((a, b) => {
		const aKey = getDisplayId(a) || '';
		const bKey = getDisplayId(b) || '';
		return aKey.localeCompare(bKey, undefined, { sensitivity: 'base' });
	});

	return (
		<div className={styles.tableContainer} style={{ maxHeight: '500px', overflow: 'auto' }}>
			<table
				className={`${styles.workplaceTable} ${styles.userTable}`}
				style={{ tableLayout: 'auto' }}
			>
				<thead>
					<tr>
						<th style={{ width: '60px', whiteSpace: 'nowrap' }}>Unit Number</th>
						<th style={{ width: '30%', minWidth: '100px' }}>Name</th>
						<th style={{ width: '25%', minWidth: '150px', whiteSpace: 'nowrap' }}>Parent</th>
						<th style={{ padding: '8px', width: '48px', textAlign: 'center' }}>Edit</th>
					</tr>
				</thead>
				<tbody>
					{organizationNode ? renderTableRow(organizationNode, true) : null}
					{workplaces.map((w) => renderTableRow(w, false))}
				</tbody>
			</table>
		</div>
	);
};

export default HierarchyList;
